import { useCallback, useEffect, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { CoreFormFieldSelect } from '@bambi/component-library/src/components/forms/fields/CoreFormFieldSelect';
import { DateRange } from '@bambi/component-library/src/components/inputs/DatePicker/DatePicker';
import DateRangePicker from '@bambi/component-library/src/components/inputs/DateRangePicker/DateRangePicker';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { DateTime } from 'luxon';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { formatDateAsTodayOrMonthDay } from 'src/common/date/formatDateAsTodayOrMonthDay';
import { OverallPaymentStatusEnum } from 'src/common/external/bambi-api/bambiApi';
import { InvoiceExportCSVArgOrdering } from 'src/common/external/bambi-api/emptyApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { MultiSelectPayerField } from 'src/common/MultiSelectPayerContextField';

import PaymentStatusFilter from '../../../filters/PaymentStatusFilter';
import { useLazyFetchInvoiceExportData } from '../useLazyFetchInvoiceExportData';

import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

const orderingOptions: SelectOption<InvoiceExportCSVArgOrdering>[] = [
  {
    label: 'Trip Date',
    value: 'trip__time_range__startswith',
  },
  {
    label: 'Passenger Name',
    value: 'trip__passenger__full_name',
  },
  {
    label: 'Payer Name',
    value: 'trip__payer__display_name',
  },
];

type InvoiceExportForm = {
  dateRange?: DateRange;
  payers?: string[];
  paymentStatus: OverallPaymentStatusEnum[];
  ordering?: InvoiceExportCSVArgOrdering;
};

interface CondensedPDFStep1Props {
  onClose?: () => void;
  next: () => void;
  prev: () => void;
}

export function CondensedPDFStep1({
  onClose,
  next,
  prev,
}: CondensedPDFStep1Props) {
  const form = useForm<InvoiceExportForm>({
    defaultValues: {
      paymentStatus: [],
      ordering: 'trip__time_range__startswith',
    },
  });
  const [validationError, setValidationError] = useState<string>();
  const {
    loading,
    error,
    trigger: fetchData,
    data,
  } = useLazyFetchInvoiceExportData();

  const handleClose = useCallback(() => {
    onClose?.();
    form.reset({
      ordering: 'trip__time_range__startswith',
    });
  }, [onClose, form]);

  const handleSubmit = form.handleSubmit(async (data) => {
    if (!data.dateRange?.from) {
      setValidationError('Please select a start date');
      return;
    }

    setValidationError(undefined);

    const rangeStart = DateTime.fromJSDate(data.dateRange.from).toISODate();

    const toDate =
      data.dateRange.to && DateTime.fromJSDate(data.dateRange.to).toISODate();

    const sameDayRange = rangeStart === toDate;

    const rangeEnd = sameDayRange ? undefined : toDate;

    await fetchData({
      rangeStart,
      rangeEnd,
      tripPayerIn: data.payers,
      format: 'pdf',
      outputType: 'condensed',
      overallPaymentStatusIn: data.paymentStatus,
      ordering: data.ordering,
    });
  });

  useEffect(() => {
    if (!loading && data && !error) {
      next();
    }
  }, [data, error, loading, next]);

  // TODO support sort by: passenger, trip date

  return (
    <div>
      {error || validationError ? (
        <FormErrorMessage>{error ?? validationError}</FormErrorMessage>
      ) : null}
      {loading ? (
        <div className="flex w-full flex-row justify-center">
          <div className="flex flex-col items-center gap-2">
            <LoadingIndicator />
            Gathering data
          </div>
        </div>
      ) : (
        <FormProvider {...form}>
          <div className="mt-2 flex flex-col gap-4">
            <strong>Filters:</strong>
            <div className="flex flex-row justify-items-stretch gap-4">
              <div>
                <div className="text-sm font-medium text-gray-700">
                  Select the date range *
                </div>
                <div className="flex items-center justify-between gap-2 pt-2">
                  <Controller
                    control={form.control}
                    name="dateRange"
                    render={({ field }) => {
                      let fromDate = '';
                      let toDate = '';

                      if (field.value?.from) {
                        fromDate = formatDateAsTodayOrMonthDay(
                          DateTime.fromJSDate(field.value.from).toISODate(),
                          true
                        );
                      }

                      if (field.value?.to) {
                        toDate = formatDateAsTodayOrMonthDay(
                          DateTime.fromJSDate(field.value.to).toISODate(),
                          true
                        );
                      }

                      return (
                        <DateRangePicker
                          side="left"
                          triggerElement={
                            <Button>
                              <AdjustmentsHorizontalIcon className="mr-2 h-4 w-4" />{' '}
                              {field.value
                                ? ` ${fromDate}${toDate ? ' - ' + toDate : ''}`
                                : 'Select Date'}
                            </Button>
                          }
                          onDateRangeChange={(r) => {
                            field.onChange(r);
                          }}
                        />
                      );
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="text-sm font-medium text-gray-700">
                  Payment status
                </div>
                <div className="flex items-center justify-between gap-2 pt-2">
                  <Controller
                    control={form.control}
                    name="paymentStatus"
                    render={({ field }) => (
                      <PaymentStatusFilter
                        onApply={field.onChange}
                        selected={field.value}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            <MultiSelectPayerField
              fieldPath="payers"
              fieldLabel="Payers"
              helperText="Leave empty to select all payers"
              rules={{}}
              canSelectAll={false}
            />
          </div>
          <div className="mt-2 flex flex-col gap-4">
            <strong>Sorting:</strong>
          </div>
          <div>
            <Controller
              control={form.control}
              name="ordering"
              render={({ field }) => (
                <CoreFormFieldSelect
                  label="Order by"
                  options={orderingOptions}
                  onChange={(value) => {
                    field.onChange(value.value);
                  }}
                  value={
                    orderingOptions.find(
                      (v) => v.value === field.value
                    ) as SelectOption<InvoiceExportCSVArgOrdering>
                  }
                  inputProps={{}}
                />
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-4 pt-6">
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              dataTestId="select-pdf-data"
              variant="primary"
              onClick={handleSubmit}
            >
              Gather data
            </Button>
          </div>
        </FormProvider>
      )}
    </div>
  );
}
